import React, { useCallback, useMemo, useState } from 'react'
import styles from './psq.module.scss'
import { Trans, useTranslation } from 'react-i18next'
import { Form, Formik, FormikErrors, FormikValues } from 'formik'
import BtnPrimary from 'components/generic/BtnPrimary'
import BtnSecondary from 'components/generic/BtnSecondary'
import Heading from 'components/generic/Heading'
import PsqProgressBar from './PsqProgressBar'
import PsqRadioOptions from './PsqRadioOptions'
import FillOnceAlert from './FillOnceAlert'
import ConfirmationModal from './ConfirmationModal'
import { RmgQA } from './psqFormData'
import PsqInputAnswer from './PsqInputAnswer'
import { PersonPSQAnswerInput } from 'types/api/types'

const QaStep = ({ qa, currentStep }: { qa: RmgQA; currentStep: number }) => {
  const { t } = useTranslation()
  const answerId = 'answer' + currentStep.toString()
  const heading = t(qa.questionTransKey, { ns: 'strPsq' })
  const introText = qa.introTextTransKey && t(qa.introTextTransKey, { ns: 'strPsq' })
  const validator = useMemo(() => qa.validator && qa.validator(t), [t, qa])

  return (
    <>
      {qa.type === 'radio' ? (
        <PsqRadioOptions
          answerId={answerId}
          heading={heading}
          introText={introText}
          opts={qa.opts}
          buttonsLayout={qa.buttonsLayout}
          validator={validator}
        />
      ) : (
        <PsqInputAnswer
          inputProps={qa.inputProps}
          answerId={answerId}
          heading={heading}
          introText={introText}
          validator={validator}
        />
      )}
    </>
  )
}

const ReviewAnswers = ({
  values,
  formData,
  updatedAnswerKey,
  setCurrentStep,
}: {
  values: FormikValues
  formData: RmgQA[]
  updatedAnswerKey: string | null
  setCurrentStep: (n: number) => void
}) => {
  const { t } = useTranslation(['strPsq', 'strCommonUI'])

  return (
    <>
      <h2 className={styles.heading}>{t('review.title')}</h2>
      <p className={styles.text}>{t('review.infoText')}</p>
      <ol className={styles.list}>
        {Object.values(values).map((value, i) => {
          const qa = formData[i]

          return (
            <li key={i} className={styles.listItem}>
              <div className={styles.questionWithEdit}>
                <span>
                  <b className={styles.index}>{i + 1}.</b>
                  {qa.type === 'radio' ? (
                    <Trans t={t}>{`${qa.opts[value - 1]}.review`}</Trans>
                  ) : (
                    <Trans t={t} values={{ value }}>
                      {qa.reviewTextTransKey}
                    </Trans>
                  )}
                </span>
                <span onClick={() => setCurrentStep(i + 1)} className={styles.editBtn}>
                  {t('button.edit', { ns: 'strCommonUI' })}
                </span>
              </div>
              {updatedAnswerKey === 'answer' + (i + 1).toString() ? (
                <div className={styles.updatedMsgContainer}>
                  <span className={styles.updatedMsg}>
                    {t('button.updated', { ns: 'strCommonUI' })}
                  </span>
                </div>
              ) : null}
            </li>
          )
        })}
      </ol>
    </>
  )
}

interface Props {
  formData: RmgQA[]
  onSubmit: (answers: PersonPSQAnswerInput[]) => void
  withOnlyFillOnceAlert?: boolean
  withConfirmationModal?: boolean
}

const Questionnaire = ({
  formData,
  onSubmit,
  withOnlyFillOnceAlert = false,
  withConfirmationModal = false,
}: Props) => {
  const { t } = useTranslation(['strPsq', 'strCommonUI'])
  const { progressBarSteps, reviewStep, startingStep, initialValues } = useMemo(() => {
    const formValues = {}
    formData.forEach((qa, i) => {
      formValues[`answer${i + 1}`] = qa.defaultValue ?? null
    })
    return {
      progressBarSteps: formData.length,
      reviewStep: formData.length + 1,
      startingStep: 1,
      initialValues: formValues,
    }
  }, [formData])

  const [currentStep, setCurrentStep] = useState(startingStep)
  const [reviewHasBeenReached, setReviewHasBeenReached] = useState<boolean>(false)
  const [previousValues, setPreviousValues] = useState<any>(null)
  const [updatedAnswerKey, setUpdatedAnswerKey] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const isStartingStep = currentStep === startingStep
  const isReview = currentStep === reviewStep

  const handleContinue = useCallback(
    values => {
      if (!reviewHasBeenReached) {
        setCurrentStep(currentStep + 1)
      }

      if (currentStep === reviewStep - 1 && !reviewHasBeenReached) {
        setReviewHasBeenReached(true)
        setPreviousValues(values)
      }

      if (reviewHasBeenReached) {
        setCurrentStep(reviewStep)
        let updated = false
        let updatedKey: null | string = null

        const compare = (obj, source) => {
          Object.keys(source).forEach(key => {
            if (obj.hasOwnProperty(key) && obj[key] !== source[key]) {
              updated = true
              updatedKey = key
            }
          })
        }

        compare(values, previousValues)

        if (updated) {
          setUpdatedAnswerKey(updatedKey)
          setPreviousValues(values)
        } else {
          setUpdatedAnswerKey(null)
        }
      }
    },
    [previousValues, reviewHasBeenReached, reviewStep, currentStep]
  )

  const handleOpenModal = useCallback(() => setIsModalOpen(true), [])
  const handleCloseModal = useCallback(() => setIsModalOpen(false), [])

  return (
    <div className={styles.psq}>
      <div>
        <Heading>{t('heading')}</Heading>
        {!isReview && (
          <p>
            <Trans ns='strPsq' i18nKey={'introMsg'}>
              Complete these simple questions for us to create you an <b>ITF World Tennis Number</b>{' '}
              until you start competing.
            </Trans>
          </p>
        )}
      </div>
      {(isStartingStep || isReview) && withOnlyFillOnceAlert && <FillOnceAlert />}
      {!isReview && <PsqProgressBar step={currentStep} totalSteps={progressBarSteps} />}
      <Formik
        initialValues={initialValues}
        validateOnChange
        onSubmit={(values, actions) => {
          const transformValues = (valuesObj): Object => {
            const array: any = []

            Object.values(valuesObj).forEach((item, i) => {
              const newObj: any = {}
              newObj.questionId = i + 1
              newObj.answerId = item
              array.push(newObj)
            })
            return array
          }
          onSubmit(transformValues(values) as any)
          actions.setSubmitting(false)
        }}
      >
        {({
          values,
          errors,
        }: {
          values: FormikValues
          isValid: boolean
          errors: FormikErrors<any>
        }) => {
          const qa = formData[currentStep - 1]
          const currentQaKey = `answer${currentStep}`
          const canContinue = values[currentQaKey] && !errors[currentQaKey]

          return (
            <Form className={styles.form}>
              {isReview ? (
                <ReviewAnswers
                  formData={formData}
                  setCurrentStep={setCurrentStep}
                  updatedAnswerKey={updatedAnswerKey}
                  values={values}
                />
              ) : (
                <QaStep qa={qa} currentStep={currentStep} />
              )}
              <div className={styles.psqBtns}>
                {!isStartingStep && (
                  <BtnSecondary
                    size={'medium'}
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className={styles.backBtn}
                  >
                    {t('button.back', { ns: 'strCommonUI' })}
                  </BtnSecondary>
                )}

                {isReview ? (
                  <>
                    <BtnPrimary
                      type={withConfirmationModal ? 'button' : 'submit'}
                      size={'medium'}
                      onClick={withConfirmationModal ? handleOpenModal : undefined}
                    >
                      {t('button.submit', { ns: 'strCommonUI' })}
                    </BtnPrimary>
                    {withConfirmationModal && (
                      <ConfirmationModal isOpen={isModalOpen} onClose={handleCloseModal} />
                    )}
                  </>
                ) : (
                  <BtnPrimary
                    size={'medium'}
                    isDisabled={!canContinue}
                    onClick={() => handleContinue(values)}
                  >
                    {t('button.continue', { ns: 'strCommonUI' })}
                  </BtnPrimary>
                )}
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default Questionnaire
